import { Routes, Route, Outlet, useOutletContext } from "react-router-dom";
import styled, { css, up } from "@xstyled/styled-components";
import { useEffect, useMemo, useState } from "react";

import { Conversation } from "./Conversation/Conversation";
import { NoMessages } from "./NoMessages";
import { MessagePlaceholder } from "./MessagePlaceholder";
import { InboxSidebar, Conversation as ConversationType } from "./InboxSidebar";

import { palette, pxToRem } from "@otta/design-tokens";
import { useQuery } from "@otta/search/apollo";
import { Loading } from "@otta/search/components/Loading";
import { pushAnalyticsEvent } from "@otta/analytics";
import { INBOX_WIDTH_DESKTOP } from "@otta/search/globalConstants";
import { InboxDocument } from "@otta/search/schema";
import { PageWrapper } from "@otta/search/components/PageWrapper";
import {
  ConversationRedirect,
  MessageRequestRedirect,
} from "@otta/search/pages/Messaging/LegacyRouteRedirects";
import { useTabVisible } from "@otta/search/hooks/useTabVisible";
import { Heading, Text } from "@otta/design";
import { useExperiment } from "@otta/search/hooks/unleash";
import { Experiment } from "@otta/search/constants/experiments";

const InboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  max-width: ${pxToRem(1200)};
  margin: 0 auto;
  position: relative;
  padding-top: lg;
`;

const InboxHeader = styled.div`
  padding-left: lg;
`;

const WarningBanner = styled.div`
  background-color: ${palette.extended.blue.shade100};
  border-left: 4px solid ${palette.brand.blue};
  border-radius: 4;
  margin: lg lg 0 0;
  padding: md;
`;

const InboxContent = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  max-width: ${pxToRem(1200)};
  margin: 0 auto;
  position: relative;
`;

const ContentWrapper = styled.div`
  overflow: hidden;
  position: relative;
  display: flex;
  flex: 1;
  margin-left: 0;
  ${up(
    "desktop",
    css`
      margin-left: ${INBOX_WIDTH_DESKTOP}px;
      padding: lg;
    `
  )}
`;

const POLL_INTERVAL = 60000;

function MessagingFallback() {
  const setShowSideMenu = useOutletContext<(s: boolean) => void>();

  useEffect(() => {
    setShowSideMenu(true);
  }, [setShowSideMenu]);

  return <MessagePlaceholder />;
}

function MessagingPage() {
  const [showSideMenu, setShowSideMenu] = useState(true);

  const visible = useTabVisible();

  const { variant } = useExperiment(Experiment.MessagingWarning);
  const messagingWarningEnabled = variant === "variant";

  const { data, loading, stopPolling, startPolling } = useQuery(InboxDocument, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      if (
        data?.me?.__typename === "CurrentAdmin" ||
        data?.me?.__typename === "CurrentCandidate"
      ) {
        pushAnalyticsEvent({
          eventName: "Candidate Viewed",
          page: "inbox",
          receivedMessages: data?.me?.conversations?.length > 0,
        });
      }
    },
  });

  useEffect(() => {
    if (visible) {
      startPolling(POLL_INTERVAL);
    } else {
      stopPolling();
    }
  }, [startPolling, stopPolling, visible]);

  const candidateData =
    data?.me?.__typename === "CurrentAdmin" ||
    data?.me?.__typename === "CurrentCandidate"
      ? data?.me
      : null;

  const conversations: ConversationType[] = useMemo(() => {
    return candidateData?.conversations ?? [];
  }, [candidateData]);

  if (loading) {
    return (
      <PageWrapper>
        <Loading />
      </PageWrapper>
    );
  }

  if (!conversations.length) {
    return (
      <NoMessages
        profileVisible={candidateData?.showProfileToCompanies ?? false}
      />
    );
  }

  return (
    <InboxWrapper>
      <InboxHeader>
        <Heading size={2} bold>
          Inbox
        </Heading>
        {messagingWarningEnabled && (
          <WarningBanner>
            <Text>
              <Text as="span" bold>
                Heads up!{" "}
              </Text>
              Messaging will temporarily shift to email in the coming days as we
              work on some exciting platform updates. Companies you're in
              contact with have your details so you won't miss a thing.
            </Text>
          </WarningBanner>
        )}
      </InboxHeader>
      <InboxContent>
        <InboxSidebar
          showSideMenu={showSideMenu}
          conversations={conversations}
          candidateId={candidateData?.id ?? ""}
        />
        <ContentWrapper>
          <Outlet context={setShowSideMenu} />
        </ContentWrapper>
      </InboxContent>
    </InboxWrapper>
  );
}

export default function Messaging(): React.ReactElement {
  return (
    <Routes>
      <Route path="/" element={<MessagingPage />}>
        <Route index element={<MessagingFallback />} />
        <Route
          path="conversations/:conversationId"
          element={<ConversationRedirect />}
        />
        <Route
          path="message-requests/:messageRequestId"
          element={<MessageRequestRedirect />}
        />
        <Route path=":conversationId" element={<Conversation />} />
      </Route>
    </Routes>
  );
}
